import {
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { groupedOrderOptions } from '@shared/constants';

/**
 * Multi-select picker for "what was purchased" on a prayer order.
 *
 * Instead of a scrolling <mat-select>, every option is laid out at once in
 * 3 columns (2 on tablets, 1 on phones), grouped by type, with a search box
 * that filters as you type. Keyboard: arrows move the highlight (←/→ jump
 * columns), Enter toggles, Backspace on an empty search removes the last
 * item, Esc closes. Emits `string[]` of option values (ControlValueAccessor).
 */

interface PickerOption {
  value: string;
  label: string;
  /** Normalised text used for matching. */
  key: string;
  /** DOM id, for aria-activedescendant. */
  id: string;
}

interface PickerGroup {
  id: string;
  label: string;
  options: PickerOption[];
}

interface Cell {
  col: number;
  row: number;
}

/** Which groups sit in which column, per column count (RTL: first = right). */
const COLUMN_PLANS: Record<number, string[][]> = {
  3: [['aliyot', 'mincha'], ['tefila', 'honors'], ['holidays', 'payments']],
  2: [['aliyot', 'mincha', 'tefila'], ['holidays', 'honors', 'payments']],
  1: [['aliyot', 'mincha', 'tefila', 'holidays', 'honors', 'payments']],
};

/** Strip hyphens, spaces and quotes so "עליה שלישי" finds "עליה-שלישי". */
function normalise(s: string): string {
  return s.replace(/[\s\-–־'"״׳]/g, '');
}

let nextUid = 0;

@Component({
  selector: 'app-order-items-picker',
  templateUrl: './order-items-picker.component.html',
  styleUrls: ['./order-items-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => OrderItemsPickerComponent),
      multi: true,
    },
  ],
})
export class OrderItemsPickerComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() placeholder = 'בחר פריטים';
  /** id of the visible <label> that names this field. */
  @Input() labelledBy: string | null = null;

  @ViewChild('trigger') triggerEl!: ElementRef<HTMLElement>;
  @ViewChild('panel') panelEl?: ElementRef<HTMLElement>;
  @ViewChild('search') searchEl?: ElementRef<HTMLInputElement>;

  readonly uid = `oip-${++nextUid}`;
  readonly groups: PickerGroup[];

  open = false;
  disabled = false;
  selected: string[] = [];
  query = '';
  colCount = 3;
  /** Visible (filtered) groups, per column. */
  columns: PickerGroup[][] = [];
  highlighted: Cell | null = null;

  private readonly labels = new Map<string, string>();
  private readonly mediaQueries: Array<{ mq: MediaQueryList; handler: () => void }> = [];
  private onChange: (value: string[]) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private host: ElementRef<HTMLElement>) {
    let n = 0;
    this.groups = groupedOrderOptions().map(({ group, options }) => ({
      id: group.id,
      label: group.label,
      options: options.map((o) => {
        this.labels.set(o.value, o.label);
        return {
          value: o.value,
          label: o.label,
          key: normalise(`${o.label} ${o.value}`),
          id: `${this.uid}-o${n++}`,
        };
      }),
    }));
  }

  ngOnInit(): void {
    // Column count follows the viewport: 3 desktop, 2 tablet, 1 phone.
    const breakpoints: Array<[string, number]> = [
      ['(min-width: 1024px)', 3],
      ['(min-width: 640px)', 2],
    ];
    const update = () => {
      const hit = breakpoints.find(([q]) => window.matchMedia(q).matches);
      const next = hit ? hit[1] : 1;
      if (next !== this.colCount) {
        this.colCount = next;
        this.recompute();
      }
    };
    for (const [q] of breakpoints) {
      const mq = window.matchMedia(q);
      mq.addEventListener('change', update);
      this.mediaQueries.push({ mq, handler: update });
    }
    update();
    this.recompute();
  }

  ngOnDestroy(): void {
    for (const { mq, handler } of this.mediaQueries) mq.removeEventListener('change', handler);
  }

  // ── ControlValueAccessor ────────────────────────────────────────────────

  writeValue(value: unknown): void {
    this.selected = Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) this.close(false);
  }

  // ── Open / close ────────────────────────────────────────────────────────

  toggle(): void {
    if (this.open) this.close();
    else this.openPanel();
  }

  openPanel(initialQuery = ''): void {
    if (this.disabled || this.open) return;
    this.open = true;
    this.query = initialQuery;
    this.highlighted = null;
    this.recompute();
    // The panel renders on the next change detection; position + focus after that.
    requestAnimationFrame(() => {
      this.reposition();
      const input = this.searchEl?.nativeElement;
      if (input) {
        input.focus();
        input.setSelectionRange(input.value.length, input.value.length);
      }
    });
  }

  close(returnFocus = true): void {
    if (!this.open) return;
    this.open = false;
    this.query = '';
    this.highlighted = null;
    this.onTouched();
    if (returnFocus) this.triggerEl?.nativeElement.focus();
  }

  /** Click anywhere outside the component closes the panel. */
  @HostListener('document:mousedown', ['$event'])
  onDocumentMousedown(event: MouseEvent): void {
    if (this.open && !this.host.nativeElement.contains(event.target as Node)) this.close(false);
  }

  /** Tabbing out of the component closes the panel. */
  @HostListener('focusout', ['$event'])
  onFocusOut(event: FocusEvent): void {
    const next = event.relatedTarget as Node | null;
    if (this.open && next && !this.host.nativeElement.contains(next)) this.close(false);
  }

  @HostListener('window:resize')
  @HostListener('window:scroll')
  onViewportChange(): void {
    if (this.open) this.reposition();
  }

  // ── Selection ───────────────────────────────────────────────────────────

  isSelected(value: string): boolean {
    return this.selected.includes(value);
  }

  labelOf(value: string): string {
    return this.labels.get(value) ?? value;
  }

  toggleOption(value: string): void {
    if (this.disabled) return;
    this.selected = this.isSelected(value)
      ? this.selected.filter((v) => v !== value)
      : [...this.selected, value];
    this.onChange(this.selected);
  }

  /** Mouse click on an option: toggle, keep typing possible on desktop. */
  clickOption(value: string, id: string): void {
    this.toggleOption(value);
    this.highlightById(id);
    if (window.matchMedia('(hover: hover)').matches) this.searchEl?.nativeElement.focus();
  }

  remove(value: string, event: Event): void {
    event.stopPropagation();
    this.toggleOption(value);
  }

  clearAll(): void {
    if (!this.selected.length) return;
    this.selected = [];
    this.onChange(this.selected);
    this.searchEl?.nativeElement.focus();
  }

  // ── Search ──────────────────────────────────────────────────────────────

  onQuery(value: string): void {
    this.query = value;
    this.recompute();
    requestAnimationFrame(() => this.reposition());
  }

  /** Rebuild the visible columns from the current query + column count. */
  private recompute(): void {
    const q = normalise(this.query.trim());
    const visible = this.groups
      .map((g) => ({ ...g, options: q ? g.options.filter((o) => o.key.includes(q)) : g.options }))
      .filter((g) => g.options.length > 0);

    const plan = COLUMN_PLANS[this.colCount] ?? COLUMN_PLANS[1];
    const planned = new Set(plan.flat());
    const cols = plan.map((ids) =>
      ids.map((id) => visible.find((g) => g.id === id)).filter((g): g is PickerGroup => !!g),
    );
    // Groups not in the plan (e.g. "אחר") go to the last column.
    const rest = visible.filter((g) => !planned.has(g.id));
    if (rest.length) cols[cols.length - 1].push(...rest);
    this.columns = cols.filter((c) => c.length > 0);

    if (q) {
      // Typing: first match is ready for Enter.
      this.highlighted = this.columns.length ? { col: 0, row: 0 } : null;
    } else if (this.highlighted && !this.cellOption(this.highlighted)) {
      this.highlighted = null;
    }
  }

  get noResults(): boolean {
    return this.columns.length === 0;
  }

  // ── Keyboard ────────────────────────────────────────────────────────────

  onTriggerKeydown(event: KeyboardEvent): void {
    if (this.disabled) return;
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.openPanel();
      return;
    }
    // Start typing straight away: opens the panel with that letter.
    if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();
      this.openPanel(event.key);
    }
  }

  onSearchKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.move(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.move(-1);
        break;
      case 'ArrowLeft': // RTL: the next column is to the left
        event.preventDefault();
        this.moveColumn(1);
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.moveColumn(-1);
        break;
      case 'Home':
        event.preventDefault();
        this.highlighted = this.columns.length ? { col: 0, row: 0 } : null;
        this.scrollHighlightedIntoView();
        break;
      case 'End': {
        event.preventDefault();
        const last = this.columns.length - 1;
        this.highlighted = last >= 0 ? { col: last, row: this.colOptions(last).length - 1 } : null;
        this.scrollHighlightedIntoView();
        break;
      }
      case 'Enter': {
        event.preventDefault(); // never submit the surrounding form
        const opt = this.cellOption(this.highlighted);
        if (!opt) return;
        this.toggleOption(opt.value);
        if (this.query) this.onQuery(''); // ready for the next item
        break;
      }
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        this.close();
        break;
      case 'Backspace':
        if (!this.query && this.selected.length) {
          event.preventDefault();
          this.toggleOption(this.selected[this.selected.length - 1]);
        }
        break;
    }
  }

  highlightById(id: string): void {
    for (let col = 0; col < this.columns.length; col++) {
      const row = this.colOptions(col).findIndex((o) => o.id === id);
      if (row !== -1) {
        this.highlighted = { col, row };
        return;
      }
    }
  }

  get highlightedId(): string | null {
    return this.cellOption(this.highlighted)?.id ?? null;
  }

  private colOptions(col: number): PickerOption[] {
    return (this.columns[col] ?? []).flatMap((g) => g.options);
  }

  private cellOption(cell: Cell | null): PickerOption | null {
    if (!cell) return null;
    return this.colOptions(cell.col)[cell.row] ?? null;
  }

  private move(delta: 1 | -1): void {
    const n = this.columns.length;
    if (!n) return;
    if (!this.highlighted) {
      this.highlighted = delta === 1 ? { col: 0, row: 0 } : { col: n - 1, row: this.colOptions(n - 1).length - 1 };
    } else {
      let { col, row } = this.highlighted;
      row += delta;
      if (row >= this.colOptions(col).length) {
        col = (col + 1) % n;
        row = 0;
      } else if (row < 0) {
        col = (col - 1 + n) % n;
        row = this.colOptions(col).length - 1;
      }
      this.highlighted = { col, row };
    }
    this.scrollHighlightedIntoView();
  }

  private moveColumn(delta: 1 | -1): void {
    const n = this.columns.length;
    if (!n) return;
    if (!this.highlighted) {
      this.highlighted = { col: 0, row: 0 };
    } else {
      const col = Math.min(n - 1, Math.max(0, this.highlighted.col + delta));
      const row = Math.min(this.highlighted.row, this.colOptions(col).length - 1);
      this.highlighted = { col, row };
    }
    this.scrollHighlightedIntoView();
  }

  private scrollHighlightedIntoView(): void {
    const id = this.highlightedId;
    if (!id) return;
    requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView({ block: 'nearest' }));
  }

  // ── Positioning ─────────────────────────────────────────────────────────

  /**
   * The panel is position: fixed so it can be wider than the field (the
   * columns need ~250px each to show "עליה-מנחה-ראשון" untruncated) and as
   * tall as the viewport allows. Its right edge lines up with the field
   * (RTL); if it wouldn't fit below, it shifts up over the field rather
   * than scrolling — every option stays visible.
   */
  private reposition(): void {
    const panel = this.panelEl?.nativeElement;
    const trigger = this.triggerEl?.nativeElement;
    if (!panel || !trigger || !this.open) return;

    const rect = trigger.getBoundingClientRect();
    const margin = 12;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const wanted = this.colCount === 3 ? 800 : this.colCount === 2 ? 560 : rect.width;
    const width = Math.min(Math.max(rect.width, wanted), vw - 2 * margin);
    const left = Math.min(Math.max(rect.right - width, margin), vw - margin - width);

    panel.style.left = `${left}px`;
    panel.style.width = `${width}px`;
    panel.style.maxHeight = `${vh - 2 * margin}px`;

    const height = panel.offsetHeight;
    let top = rect.bottom + 6;
    if (top + height > vh - margin) top = Math.max(margin, vh - margin - height);
    panel.style.top = `${top}px`;
  }
}
